import {
  AgentReassignMetadata,
  AutoAssignMetadata,
  HANDLED_NOTIFICATION_CODES,
  NotificationCode,
  NotificationMetadata,
} from '@customTypes/api/Notification';
import useDeleteNotification from '@hooks/reactQueryHooks/notification/useDeleteNotification';
import useGetNotification from '@hooks/reactQueryHooks/notification/useGetNotification';
import usePutNotificationRead from '@hooks/reactQueryHooks/notification/usePutNotificationRead';
import pathnames from '@src/app/routes/pathnames';
import { AGENT_TYPE_FOR_SETTINGS } from '@src/constants';
import QueryParamKeys from '@src/constants/queryParams';
import { showSuccess, showError } from '@src/utils/Toastify';
import {
  SortDirections,
  doNothing,
  parseValue,
  sortList,
} from '@src/utils/utils';
import { t } from 'i18next';
import React, {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { useHistory } from 'react-router-dom';

type AppHistory = ReturnType<typeof useHistory>;

type LeadNavigationMetadata = {
  agent_id?: string;
  application_group_id?: string;
  application_id?: string;
  variables?: {
    agent_id?: string;
    application_group_id?: string;
    application_id?: string;
  };
};

// BAE-3995: ApplicationRequestCancel (SPV cancellation-request bell),
// ApplicationRejected (SPV lead-rejected bell, BAE-3994), ApplicationCancelled
// (BAE-3996) and AssistedJourneySelected (SPV customer-needs-help bell,
// BAE-4129) all share the same navigation fields and the same list
// fallback — one helper for all, normalizing both historical variables
// and new direct flat metadata.
function navigateToLeadFromMetadata(
  history: AppHistory,
  metadata?: NotificationMetadata,
) {
  const meta = metadata as LeadNavigationMetadata | undefined;

  const agentId = meta?.variables?.agent_id ?? meta?.agent_id;
  const applicationGroupId =
    meta?.variables?.application_group_id ?? meta?.application_group_id;
  const applicationId = meta?.variables?.application_id ?? meta?.application_id;

  if (agentId && applicationGroupId && applicationId) {
    history.push({
      pathname: pathnames.leadsTrackerDetail(
        agentId,
        applicationGroupId,
        applicationId,
      ),
    });
    return;
  }

  history.push({ pathname: pathnames.leadsTracker() });
}

export type FlattenNotificationDetail = {
  created_at: string;
  description: string;
  id: string;
  link: string | null;
  read: boolean;
  title: string;
} & (
  | {
      code: NotificationCode.AutoAssign;
      metadata: AutoAssignMetadata;
    }
  | {
      code: NotificationCode.AgentReassign;
      metadata: AgentReassignMetadata;
    }
);

export type FlattenAutoAssignNotificationDetail = Extract<
  FlattenNotificationDetail,
  { code: NotificationCode.AutoAssign }
>;

export type FlattenAgentReassignNotificationDetail = Extract<
  FlattenNotificationDetail,
  { code: NotificationCode.AgentReassign }
>;

export type OnNotificationClick = (payload: {
  notificationCode: NotificationCode;
  notificationId: string;
  isRead: boolean;
  metadata?: NotificationMetadata;
}) => void;

interface NotificationContextProps {
  notificationQuery: ReturnType<typeof useGetNotification>;
  notificationReadQuery: ReturnType<typeof usePutNotificationRead>;
  notificationDeleteQuery: ReturnType<typeof useDeleteNotification>;
  total: number;
  unread: number;
  notificationList: FlattenNotificationDetail[];
  onNotificationClick: OnNotificationClick;
  handleNotificationDelete: (
    notificationIds: string[],
    callback?: () => void,
  ) => void;
}

const NotificationContext = createContext<NotificationContextProps>({
  notificationQuery: {} as ReturnType<typeof useGetNotification>,
  notificationReadQuery: {} as ReturnType<typeof usePutNotificationRead>,
  notificationDeleteQuery: {} as ReturnType<typeof useDeleteNotification>,
  total: 0,
  unread: 0,
  notificationList: [],
  onNotificationClick: doNothing,
  handleNotificationDelete: doNothing,
});

const NotificationContextProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const history = useHistory();
  const notificationQuery = useGetNotification();
  const notificationReadQuery = usePutNotificationRead();
  const notificationDeleteQuery = useDeleteNotification();

  const { total, unread, notificationList } = useMemo(() => {
    const result = notificationQuery.data?.notifications.reduce<{
      total: number;
      unread: number;
      notificationList: FlattenNotificationDetail[];
    }>(
      (acc, current) => {
        if (HANDLED_NOTIFICATION_CODES.includes(current.code)) {
          const flattenDetails = current.details.map(detail => ({
            ...detail,
            code: current.code,
          })) as FlattenNotificationDetail[];

          return {
            total: acc.total + current.total,
            unread: acc.unread + current.total_unread,
            notificationList: [...acc.notificationList, ...flattenDetails],
          };
        }

        return acc;
      },
      { notificationList: [], total: 0, unread: 0 },
    );

    return parseValue(result, {
      notificationList: [],
      total: 0,
      unread: 0,
    });
  }, [notificationQuery.data?.notifications]);

  const sortedNotificationList = useMemo(
    () => sortList(notificationList, 'created_at', SortDirections.Descending),
    [notificationList],
  );

  const onNotificationClick = useCallback(
    ({
      notificationCode,
      notificationId,
      isRead,
      metadata,
    }: {
      notificationCode: NotificationCode;
      notificationId: string;
      isRead: boolean;
      metadata?: NotificationMetadata;
    }) => {
      if (!isRead) {
        notificationReadQuery.mutate(notificationId, {
          onSuccess: () => {
            notificationQuery.refetch();
          },
        });
      }

      switch (notificationCode) {
        case NotificationCode.AutoAssign:
        case NotificationCode.AgentReassign:
          history.push({
            pathname: pathnames.agentManagement(),
            search: `?${QueryParamKeys.Type}=${AGENT_TYPE_FOR_SETTINGS.ACTIVE_AGENTS_WITH_SPV_UPLINE}`,
          });
          break;
        case NotificationCode.AgentBlacklist:
          history.push({
            pathname: pathnames.agentManagement(),
            search: `?${QueryParamKeys.Type}=${AGENT_TYPE_FOR_SETTINGS.BLACKLIST_AGENTS}`,
          });
          break;
        case NotificationCode.ApplicationRequestCancel:
        case NotificationCode.ApplicationRejected:
        case NotificationCode.ApplicationCancelled:
        case NotificationCode.AssistedJourneySelected:
          navigateToLeadFromMetadata(history, metadata);
          break;
        case NotificationCode.RecruitAssigned:
        case NotificationCode.AssetPriceRequest:
        case NotificationCode.AgentSubmitLead:
        default:
          return;
      }
    },
    [history, notificationQuery, notificationReadQuery],
  );

  const handleNotificationDelete = useCallback(
    (notificationIds: string[], callback?: () => void) => {
      notificationDeleteQuery.mutate(notificationIds, {
        onSuccess: () => {
          notificationQuery.refetch();
          showSuccess(
            t('notification.deleteSuccess', { count: notificationIds.length }),
          );
        },
        onError: () => {
          showError(
            t('notification.deleteFailed', { count: notificationIds.length }),
          );
        },
        onSettled: callback,
      });
    },
    [notificationDeleteQuery, notificationQuery],
  );

  const value = useMemo(
    () => ({
      notificationQuery,
      notificationReadQuery,
      notificationDeleteQuery,
      total,
      unread,
      notificationList: sortedNotificationList,
      onNotificationClick,
      handleNotificationDelete,
    }),
    [
      handleNotificationDelete,
      notificationDeleteQuery,
      notificationQuery,
      notificationReadQuery,
      onNotificationClick,
      sortedNotificationList,
      total,
      unread,
    ],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

const useNotificationContext = () => {
  return useContext(NotificationContext);
};

export {
  NotificationContext,
  NotificationContextProvider,
  useNotificationContext,
};
